import React, { useContext, useState } from 'react';
import ReactDOM from 'react-dom';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {faPaperPlane, faUser, faTimes, faFileInvoice} from '@fortawesome/free-solid-svg-icons';
import { toast } from 'react-toastify';
import {useManagePostRequest} from '../../hooks/useManagePostRequest/useManagePostRequest';
import {reenviarTokenNuevoPostulanteAPI, darDeBajaPostulanteAPI, getExpedientePostulacionAPI} from '../../consumers/backendApisUrls';
import ModalExpediente from './ModalExpediente';
import { AuthContext } from '../../hooks/context/authContext';
import {useGetRequest} from '../../hooks/useGetRequest/useGetRequest';

const TableWebBody = ({
  filas,
  manageSearch,
  onEditPostulante,
  onUpdatePostulanteStatus,
  onUpdatePostulanteFechaLimite,
  onCorreoEnviado,
  isLoading = false
}) => {
  const { state } = useContext(AuthContext);
  const filasArray = Array.isArray(filas) ? filas : [];

  const [executePost] = useManagePostRequest();
  const [executeGetRequest] = useGetRequest();
  
  const [checkedRows, setCheckedRows] = useState(new Set());

  const toggleRowCheck = (idPostulacion) => {
    setCheckedRows(prev => {
      const next = new Set(prev);
      if (next.has(idPostulacion)) next.delete(idPostulacion);
      else next.add(idPostulacion);
      return next;
    });
  };

  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [postulanteADarDeBaja, setPostulanteADarDeBaja] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [showModalExpediente, setShowModalExpediente] = useState(false);
  const [idPostulacionExpediente, setIdPostulacionExpediente] = useState(null);
  const [expedientePayload, setExpedientePayload] = useState(null);
  const [isLoadingExpediente, setIsLoadingExpediente] = useState(false);

  const getEstadoBadgeStyle = (idEstado) => {
    switch (Number(idEstado)) {
      case 47: // NUEVO
        return { backgroundColor: '#2563EB', color: '#FFFFFF' };
      case 18: // ENVIADO
        return { backgroundColor: '#06B6D4', color: '#FFFFFF' };
      case 16: // PROCESO
        return { backgroundColor: '#F97316', color: '#FFFFFF' };
      case 9: // COMPLETADA
        return { backgroundColor: '#22C55E', color: '#FFFFFF' };
      case 11: // VENCIDO
        return { backgroundColor: '#FACC15', color: '#111827' };
      case 25: // CANCELADO
        return { backgroundColor: '#DC2626', color: '#FFFFFF' };
      default:
        return { backgroundColor: '#64748B', color: '#FFFFFF' };
    }
  };

  const canViewExpediente = (fila) => {
    const allowedById = [16, 9, 11, 25]; // PROCESO, COMPLETADA, VENCIDO, CANCELADO
    const idEstado = Number(fila?.idEstado);
    if (allowedById.includes(idEstado)) {
      return true;
    }

    const estado = (fila?.estadoPostulante || '').toString().toLowerCase();
    return estado.includes('proceso')
      || estado.includes('completad')
      || estado.includes('vencid')
      || estado.includes('cancel');
  };

  if (isLoading) {
    return (
      <tbody>
        <tr>
          <td colSpan="10" className="text-center py-5">
            <div className="spinner-border text-primary" role="status">
              <span className="sr-only">Cargando...</span>
            </div>
            <div className="mt-2">Actualizando lista...</div>
          </td>
        </tr>
      </tbody>
    );
  }

  const estadoNombre = (fila) => {
    if (fila?.estadoPostulante) return fila.estadoPostulante;
    const id = Number(fila?.idEstado || fila?.IdEstado);
    switch (id) {
      case 47: return 'Nuevo';
      case 18: return 'Enviado';
      case 16: return 'Proceso';
      case 9:  return 'Completada';
      case 11: return 'Vencido';
      case 25: return 'Cancelado';
      default: return 'Sin estado';
    }
  };

  const handleSendCorreo = async (idPostulacion) => {
    const api = reenviarTokenNuevoPostulanteAPI(idPostulacion);
    await executePost(api, {}, (response) => {
      toast.success('Se envió el correo correctamente', { autoClose: 5000 });
      if (onUpdatePostulanteFechaLimite) {
        onUpdatePostulanteFechaLimite(idPostulacion);
      }

      if (onCorreoEnviado) {
        onCorreoEnviado(idPostulacion);
      }
    }, false);
  };

  const handleClickDarDeBaja = (postulante) => {
    setPostulanteADarDeBaja(postulante);
    setShowConfirmModal(true);
  };
  const handleCloseConfirmModal = () => {
    setShowConfirmModal(false);
    setPostulanteADarDeBaja(null);
  };

  const handleVerExpediente = async (idPostulacion) => {
    setIdPostulacionExpediente(idPostulacion);
    setExpedientePayload(null);
    setIsLoadingExpediente(true);
    setShowModalExpediente(true);

    try {
      const response = await executeGetRequest(getExpedientePostulacionAPI(idPostulacion));
      if (response && response.status === 200 && response.data) {
        setExpedientePayload(response.data);
      }
    } catch (error) {
      // Si falla el servicio, mostramos mensaje de error en el modal.
      setExpedientePayload(null);
    } finally {
      setIsLoadingExpediente(false);
    }
  };

  const handleCloseModalExpediente = () => {
    setShowModalExpediente(false);
    setIdPostulacionExpediente(null);
    setExpedientePayload(null);
    setIsLoadingExpediente(false);
  };

  const handleConfirmDarDeBaja = async () => {
    if (!postulanteADarDeBaja) return;
    
    setIsProcessing(true);
    
    if (onUpdatePostulanteStatus) {
      onUpdatePostulanteStatus(postulanteADarDeBaja.idPostulacion, "Procesando...", true);
    }
    
    const api = darDeBajaPostulanteAPI(postulanteADarDeBaja.idPostulacion);
    const usuarioBaja = state?.user?.email || state?.user?.username || 'sistema';
    const payload = { UsuarioBaja: usuarioBaja };
    
    try {
      await executePost(api, payload, (response) => {
        toast.success('Se dió de baja la postulación', { autoClose: 5000 });
        handleCloseConfirmModal();
        if (onUpdatePostulanteStatus) {
          onUpdatePostulanteStatus(postulanteADarDeBaja.idPostulacion, "Cancelado", false);
        } else {
          if (manageSearch) {
            manageSearch();
          }
        }
      }, false, (error) => {
        if (onUpdatePostulanteStatus) {
          onUpdatePostulanteStatus(postulanteADarDeBaja.idPostulacion, postulanteADarDeBaja.estadoPostulante || "Nuevo", false);
        }
      });
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <>
      <tbody className="listar-tbody">
        {filasArray.map((fila, i) => (
          <tr key={`${i}-${fila.idPostulacion}`}>
            <td className="text-center">{fila?.fechaRegistro}</td>
            <td className="">
                <div className='d-flex flex-column'>
                    <label className='m-0'>
                        {fila?.nombres} {fila?.apellidoPaterno} {fila?.apellidoMaterno}
                    </label>
                    <label className='m-0'>
                        {fila?.tipoDocumento} - {fila?.numeroDocumento}
                    </label>
                </div>
            </td>
            <td className="">
                <div className='d-flex flex-column'>
                    <label className='m-0'>
                        {fila?.celular}
                    </label>
                    <label className='m-0'>
                        {fila?.email}
                    </label>
                </div>
            </td>
            <td className="text-center" style={{ minWidth: "120px" }}>
              <span 
                className="badge" 
                style={{ 
                  backgroundColor: '#28c76f', 
                  color: '#ffffff', 
                  fontWeight: '500',
                  fontSize: '0.8rem'
                }}
              >
                {fila?.tipoEvaluacion || fila?.tipoCargo || fila?.tipoEvaluacionNombre || 'No especificado'}
              </span>
            </td>
            <td className="">{fila?.cargo || fila?.cargoNombre || fila?.nombreCargo || '-'}</td>
            {/* <td className="text-center">{fila?.nivel || '-'}</td> */}
            <td className="text-center">{fila?.especialidad || fila?.especialidadNombre || (fila?.idEspecialidad ? fila.idEspecialidad : '-')}</td>
            {/* <td className="text-center">
              <span className="badge badge-info">{fila?.paso ? `Paso ${fila.paso}` : 'Paso -'}</span>
            </td> */}
            <td className="text-center" style={{ minWidth: "180px" }}>
              <small className="text-center">
                {fila?.responsable ? fila.responsable.split('@')[0] : 'No especificado'}
              </small>
            </td>
            <td className="text-center" style={{ minWidth: "120px" }}>
              {fila?.fechaLimiteEvaluacion ? (
                <span className="text-dark" style={{ fontWeight: 600 }}>
                  {fila?.fechaLimiteEvaluacion}
                </span>
              ) : (
                <small className="text-muted">-</small>
              )}
            </td>
            <td className="text-center">
              <span className="badge" style={getEstadoBadgeStyle(fila?.idEstado || fila?.IdEstado)}>
                {estadoNombre(fila)}
              </span>
            </td>
            <td className="text-center">
              <div className="d-flex justify-content-start align-items-center">
                {fila?.estadoPostulante === "Procesando..." ? (
                  <span className="text-muted small">
                    <i className="fas fa-spinner fa-spin mr-1"></i>
                    Procesando...
                  </span>
                ) : (
                  <>
                    <FontAwesomeIcon
                      icon={faUser}
                      className="ml-3 hand-pointer blue-standard-color"
                      title={fila?.idEstado === 47 ? 'Editar postulante' : 'Ver información del postulante'}
                      size='lg'
                      onClick={() => onEditPostulante && onEditPostulante(fila.idPostulacion, fila?.idEstado === 47)}
                      style={{ cursor: 'pointer' }}
                    />
                    <input
                      type="checkbox"
                      className="ml-3"
                      title="Marcar para habilitar envío de correo"
                      checked={checkedRows.has(fila.idPostulacion)}
                      onChange={() => toggleRowCheck(fila.idPostulacion)}
                      style={{ cursor: 'pointer', width: '16px', height: '16px', accentColor: '#007bff' }}
                    />
                    <FontAwesomeIcon 
                      icon={faPaperPlane}
                      className={`ml-3 ${((fila?.estadoPostulante || '').toString().toLowerCase().includes('completad') || (fila?.estadoPostulante || '').toString().toLowerCase().includes('cancel') || fila?.idEstado === 19 || fila?.idEstado === 25) ? 'text-muted' : 'hand-pointer blue-standard-color'}`}
                      title={((fila?.estadoPostulante || '').toString().toLowerCase().includes('completad') || (fila?.estadoPostulante || '').toString().toLowerCase().includes('cancel') || fila?.idEstado === 19 || fila?.idEstado === 25) ? 'Envío no disponible para estado Completada o Cancelado' : 'Enviar Correo'}
                      size='lg'
                      onClick={() => {
                        const isDisabled = (fila?.estadoPostulante || '').toString().toLowerCase().includes('completad') || (fila?.estadoPostulante || '').toString().toLowerCase().includes('cancel') || fila?.idEstado === 19 || fila?.idEstado === 25;
                        if (isDisabled) return;
                        if (!checkedRows.has(fila.idPostulacion)) {
                          toast.warning('Debes marcar el checkbox para enviar el correo');
                          return;
                        }
                        handleSendCorreo(fila.idPostulacion);
                      }}
                      style={{ cursor: ((fila?.estadoPostulante || '').toString().toLowerCase().includes('completad') || (fila?.estadoPostulante || '').toString().toLowerCase().includes('cancel') || fila?.idEstado === 19 || fila?.idEstado === 25) ? 'not-allowed' : 'pointer' }}
                    />
                    <FontAwesomeIcon
                      icon={faFileInvoice}
                      className={`ml-3 ${canViewExpediente(fila) ? 'hand-pointer blue-standard-color' : 'text-muted'}`}
                      title={canViewExpediente(fila)
                        ? 'Ver expediente completo y notas'
                        : 'Expediente disponible solo para estados Proceso, Completada, Vencido o Cancelado'}
                      size='lg'
                      onClick={() => canViewExpediente(fila) && handleVerExpediente(fila.idPostulacion)}
                      style={{ cursor: canViewExpediente(fila) ? 'pointer' : 'not-allowed' }}
                    />
                    {fila?.idEstado !== 25 && fila?.idEstado !== 19 && fila?.estadoPostulante !== 'Cancelado' && (
                      <FontAwesomeIcon 
                        icon={faTimes}
                        className="hand-pointer ml-3 text-danger"
                        title="Dar de baja postulación"
                        size='lg'
                        onClick={() => handleClickDarDeBaja(fila)}
                      />
                    )}
                  </>
                )}
              </div>
            </td>
          </tr>
        ))}
      </tbody>

      {showConfirmModal && ReactDOM.createPortal(
        <div className="modal fade show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header bg-danger text-white">
                <h5 className="modal-title">
                  <i className="fas fa-exclamation-triangle mr-2"></i>
                  Confirmar dar de baja
                </h5>
                <button type="button" className="close text-white" onClick={handleCloseConfirmModal}>
                  <span>&times;</span>
                </button>
              </div>
              <div className="modal-body text-center py-4">
                <p className="mb-2">¿Está seguro que desea dar de baja la postulación de:</p>
                <p className="font-weight-bold h5">
                  {postulanteADarDeBaja?.nombres} {postulanteADarDeBaja?.apellidoPaterno} {postulanteADarDeBaja?.apellidoMaterno}
                </p>
                <p className="text-muted">
                  DNI: {postulanteADarDeBaja?.numeroDocumento}
                </p>
              </div>
              <div className="modal-footer justify-content-center">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={handleCloseConfirmModal}
                  disabled={isProcessing}
                >
                  No, cancelar
                </button>
                <button
                  type="button"
                  className="btn btn-danger"
                  onClick={handleConfirmDarDeBaja}
                  disabled={isProcessing}
                >
                  {isProcessing ? (
                    <>
                      <span className="spinner-border spinner-border-sm mr-2" role="status"></span>
                      Procesando...
                    </>
                  ) : (
                    'Sí, dar de baja'
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}

      {showModalExpediente && (
        <ModalExpediente
          idPostulacion={idPostulacionExpediente}
          expedientePayload={expedientePayload}
          isLoadingExpediente={isLoadingExpediente}
          isOpen={showModalExpediente}
          onClose={handleCloseModalExpediente}
        />
      )}
    </>
  );
};

export default TableWebBody;
